import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, forkJoin, from, map, of, switchMap, tap, throwError } from 'rxjs';
import { randomString } from './lib/random-string';
import { PICFLOW_CONFIG } from './picflow-config';
import { NetworkService } from './services/network.service';
import { StateService } from './services/state.service';

@Injectable({
  providedIn: 'root',
})
export class NetworkInterceptorService {
  public request$ = new BehaviorSubject<HttpRequest<unknown>>(undefined);
  public response$ = new BehaviorSubject<{ res: HttpResponse<unknown>; req: HttpRequest<unknown> }>(undefined);
  public error$ = new BehaviorSubject<{ error: HttpErrorResponse; req: HttpRequest<unknown> }>(undefined);
}

@Injectable()
export class NetworkInterceptor implements HttpInterceptor {
  private errorWrapper = inject<{ error: Error }>('ERROR_WRAPPER' as never, { optional: true });
  private platformId = inject<string>(PLATFORM_ID);
  private config = inject(PICFLOW_CONFIG);
  private state = inject(StateService);
  private networkInterceptor = inject(NetworkInterceptorService);
  private networkService = inject(NetworkService);

  // intercept function
  public intercept(req: HttpRequest<unknown>, next: HttpHandler, errorCount = 0): Observable<HttpEvent<unknown>> {
    // returning an observable to complete the request cycle
    this.networkInterceptor.request$.next(req);

    return of(this.addRandomParam(req))
      .pipe(
        switchMap(_req => this.addAuthHeaders(_req, errorCount)),
        map(_req => this.addTenant(_req)),
        switchMap(_req => next.handle(_req))
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          errorCount++;
          const retryCount = this.getRetryCount(req, err);
          const delay = this.getRetryDelay(err);

          if (errorCount > retryCount) return this.handleError(req, err);
          return delay.pipe(switchMap(() => this.intercept(req, next, errorCount)));
        }),
        tap((res: HttpResponse<unknown>) => this.networkInterceptor.response$.next({ res, req }))
      );
  }

  private getRetryDelay(error: HttpErrorResponse): Observable<unknown> {
    if (isPlatformServer(this.platformId)) return of(undefined);
    if (error.status === 404 || error.status === 422) return of(undefined);
    return forkJoin([this.networkService.online$, of(1000)]);
  }

  private getRetryCount(req: HttpRequest<unknown>, error: HttpErrorResponse) {
    if (isPlatformServer(this.platformId)) return 0;
    if (error.status === 404 || error.status === 422) return 0;
    if (req.url.includes('/internal-api/')) return 0;
    if (req.url.includes('.amazonaws.com')) {
      if (error.status === 0) return 3;
    }
    return 1;
  }

  private handleError(req: HttpRequest<unknown>, error: HttpErrorResponse) {
    if (req.url.includes('ignore_errors')) return throwError(() => error);
    if (req.url.includes('ignore_401_error') && [401, 403].includes(error.status)) return throwError(() => error);
    this.networkInterceptor.error$.next({ error: error, req });

    const code = error?.error?.code;
    if (code) {
      if (error.status === 503 && code === 'maintenance_exception') {
        if (!req.url.includes('ignore_503_error')) {
          if (isPlatformBrowser(this.platformId)) {
            const returnUrl = location.href.replace(location.origin, '');
            location.href = `${location.origin}/maintenance?returnUrl=${encodeURIComponent(returnUrl)}`;
          }
          if (this.errorWrapper) this.errorWrapper.error = error;
        }
      }
    }
    return throwError(() => error);
  }

  private addTenant(req: HttpRequest<unknown>) {
    const apiRequest = req.url.includes(this.config.apiEndpoint);
    if (!apiRequest) return req;

    const tenantId =
      this.state.activeTenant?.id || this.state.sharedWithMeActiveTenant?.id || this.state.tenants?.[0]?.id;

    let url = req.url;
    const addQueryParam = (url: string, key: string, value: string) => {
      if (url.includes('?')) return `${url}&${key}=${value}`;
      return `${url}?${key}=${value}`;
    };

    if (tenantId) url = addQueryParam(url, 'picflow-tenant', tenantId);
    if (this.config.tenantPathFromDomain)
      url = addQueryParam(url, 'picflow-tenant-path', this.config.tenantPathFromDomain);

    return req.clone({ url });
  }

  private addRandomParam(req: HttpRequest<unknown>) {
    const apiRequest = req.url.includes(this.config.apiEndpoint);
    if (!apiRequest) return req;
    return req.clone({ url: req.url + (req.url.includes('?') ? '&' : '?') + 'r=' + randomString(5) });
  }

  private addAuthHeaders(req: HttpRequest<unknown>, errorCount: number) {
    const apiRequest = req.url.includes(this.config.apiEndpoint);
    const requiresAuth = apiRequest || req.url.includes('with-auth');
    const template = req.url.split('with-auth=')[1]?.split('&')[0];

    const guestAuthRequest = req.url.includes('/auth/guest');
    if (!requiresAuth) return of(req);
    if (guestAuthRequest) return of(req);

    const headers: { [name: string]: string | string[] } = {};

    if (isPlatformServer(this.platformId)) {
      headers['x-ssr-origin'] = this.config.origin;
    }

    const guest = !!this.state.guestAuthToken;
    const user = guest && req.url.includes('force-guest') ? undefined : this.state.tokenData;
    const observable = user ? this.getUserHeaders(template, errorCount > 0) : guest ? this.getGuestHeaders() : of({});

    return observable.pipe(
      map(authHeaders => {
        return req.clone({ setHeaders: { ...authHeaders, ...headers } });
      })
    );
  }

  private getGuestHeaders() {
    return from(this.state.renewGuestAuthToken()).pipe(
      map(() => {
        if (!this.state.guestAuthToken) {
          console.warn('guest_token_rm'); // guest token was removed during renewGuestAuthToken
          return of({}); // if token was removed during renewGuestAuthToken
        }
        return {
          authorization: `Bearer ${this.state.guestAuthToken}`,
        };
      })
    );
  }

  private getUserHeaders(template?: string, skipCache?: boolean) {
    return from(this.state.tokenData.getIdToken(template, skipCache)).pipe(
      map(token => {
        const headers: { [name: string]: string | string[] } = {
          Authorization: `Bearer ${token}`,
        };
        return headers;
      })
    );
  }
}
